/**
 * Image generation model registry.
 *
 * Each entry describes the capabilities and constraints of a single image
 * generation model. UI components read this registry to render model-specific
 * selectors and enforce upload restrictions.
 */

export type ImageModalityType = 'Text to Image' | 'Image to Image' | 'Text+Image to Image';

export type NativeOutput = '512' | '1K' | '1.5K' | '2K' | '3K' | '4K';

export type FlexibleRatio =
  | '1:1'
  | '3:2'
  | '2:3'
  | '3:4'
  | '4:3'
  | '4:5'
  | '5:4'
  | '9:16'
  | '16:9'
  | '21:9'
  | '1:4'
  | '4:1'
  | '1:8'
  | '8:1'
  | '19.5:9'
  | '20:9'
  | '2:1'
  | '1:2'
  | '3:1'
  | '1:3'
  | '17:9'
  | 'auto';

export type ImageFormat = 'PNG' | 'JPEG' | 'WebP' | 'HEIC' | 'HEIF' | 'BMP' | 'TIFF' | 'AVIF';

export interface ImageModelSpec {
  /** Model id as used in AIModel.id (providerCode:modelCode). */
  modelId: string;
  modalities: ImageModalityType[];
  nativeOutputs: NativeOutput[];
  flexibleRatios: FlexibleRatio[];
  maxInputImages: number;
  maxOutputImages: number;
  inputFormats: ImageFormat[];
  outputFormats: ImageFormat[];
  /** Maximum size of a single input image in bytes. */
  imageSizeMaxBytes: number;
  /** Whether the model supports transparent/opaque background selection. */
  supportsBackground?: boolean;
  /** Optional quality levels for generation (model-specific). */
  qualityOptions?: Array<'Low' | 'Medium' | 'High'>;
}

/** Convert a human-friendly size string ("30MB", "9MB") to bytes. */
function mb(n: number): number {
  return n * 1024 * 1024;
}

export const IMAGE_MODEL_SPECS: ImageModelSpec[] = [
  {
    modelId: 'google:gemini-2.5-flash-image',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K', '4K'],
    flexibleRatios: ['1:1', '3:2', '2:3', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
    maxInputImages: 14,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP', 'HEIC', 'HEIF'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'google:gemini-2.5-flash-image-lite',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K'],
    flexibleRatios: ['1:1', '3:2', '2:3', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
    maxInputImages: 14,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP', 'HEIC', 'HEIF'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'google:gemini-3-pro-image',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K', '4K'],
    flexibleRatios: ['1:1', '3:2', '2:3', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
    maxInputImages: 3,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP', 'HEIC', 'HEIF'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'google:gemini-3-pro-image-2',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['512', '1K', '2K', '4K'],
    flexibleRatios: [
      '1:1',
      '3:2',
      '2:3',
      '3:4',
      '4:3',
      '4:5',
      '5:4',
      '9:16',
      '16:9',
      '21:9',
      '1:4',
      '4:1',
      '1:8',
      '8:1',
    ],
    maxInputImages: 14,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'openai:gpt-image-1-mini',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '1.5K'],
    flexibleRatios: ['1:1', '3:2', '2:3'],
    maxInputImages: 1,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(20),
  },
  {
    modelId: 'openai:gpt-image-1.5',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K', '4K'],
    flexibleRatios: ['1:1', '3:2', '2:3'],
    maxInputImages: 6,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(50),
    supportsBackground: true,
    qualityOptions: ['Low', 'Medium', 'High'],
  },
  {
    modelId: 'openai:gpt-image-2',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K', '4K'],
    flexibleRatios: ['1:1', '3:4', '4:3', '16:9', '9:16'],
    maxInputImages: 6,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(50),
  },
  {
    modelId: 'xai:grok-2-image',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K'],
    flexibleRatios: ['1:1', '1:2', '2:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'],
    maxInputImages: 3,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'bfl:flux-pro-ultra',
    modalities: ['Text to Image'],
    nativeOutputs: ['2K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '21:9'],
    maxInputImages: 10,
    maxOutputImages: 6,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(9),
  },
  {
    modelId: 'bfl:flux-pro-2',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
    maxInputImages: 10,
    maxOutputImages: 6,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(9),
  },
  {
    modelId: 'bfl:flux-2.0',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
    maxInputImages: 4,
    maxOutputImages: 6,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(9),
  },
  {
    modelId: 'bfl:flux-2.0-turbo',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
    maxInputImages: 4,
    maxOutputImages: 6,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(9),
  },
  {
    modelId: 'bfl:flux-2.0-flash',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
    maxInputImages: 4,
    maxOutputImages: 6,
    inputFormats: ['PNG', 'JPEG', 'WebP'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(9),
  },
  {
    modelId: 'kling:kling-v3-omni',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K', '4K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '21:9', 'auto'],
    maxInputImages: 10,
    maxOutputImages: 9,
    inputFormats: ['PNG', 'JPEG'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(10),
  },
  {
    modelId: 'kling:kling-image-v3',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '21:9'],
    maxInputImages: 1,
    maxOutputImages: 9,
    inputFormats: ['PNG', 'JPEG'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(10),
  },
  {
    modelId: 'bytedance:seedream-5-pro',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '21:9'],
    maxInputImages: 10,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG', 'WebP', 'HEIC', 'HEIF', 'BMP', 'TIFF'],
    outputFormats: ['PNG', 'JPEG'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'bytedance:seedream-5-0',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['2K', '3K', '4K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '2:3', '3:2', '21:9'],
    maxInputImages: 10,
    maxOutputImages: 5,
    inputFormats: ['PNG', 'JPEG', 'WebP', 'HEIC', 'HEIF', 'BMP', 'TIFF'],
    outputFormats: ['PNG', 'JPEG'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'bytedance:seedream-4-5',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['1K', '2K', '4K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
    maxInputImages: 10,
    maxOutputImages: 6,
    inputFormats: ['PNG', 'JPEG'],
    outputFormats: ['PNG', 'JPEG'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'luma:luma-uni-1',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['4K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '3:2', '2:3', '3:1', '1:3', '2:1', '1:2'],
    maxInputImages: 1,
    maxOutputImages: 4,
    inputFormats: ['PNG', 'JPEG'],
    outputFormats: ['PNG', 'JPEG'],
    imageSizeMaxBytes: mb(30),
  },
  {
    modelId: 'qwen:qwen-image-3',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['2K'],
    flexibleRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
    maxInputImages: 1,
    maxOutputImages: 1,
    inputFormats: ['PNG', 'JPEG', 'WebP', 'BMP', 'TIFF'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(10),
  },
  {
    modelId: 'reve:reve-2-1',
    modalities: ['Text to Image', 'Image to Image', 'Text+Image to Image'],
    nativeOutputs: ['4K'],
    flexibleRatios: [
      '4:1',
      '3:1',
      '21:9',
      '2:1',
      '17:9',
      '16:9',
      '3:2',
      '4:3',
      '5:4',
      '1:1',
      '4:5',
      '3:4',
      '2:3',
      '9:16',
      '1:2',
      '1:3',
      '1:4',
      'auto',
    ],
    maxInputImages: 1,
    maxOutputImages: 8,
    inputFormats: ['PNG', 'JPEG', 'WebP', 'AVIF', 'HEIF'],
    outputFormats: ['PNG', 'JPEG', 'WebP'],
    imageSizeMaxBytes: mb(10),
  },
];

/** Canonical image model ids from the latest user specification. */
export const IMAGE_MODEL_IDS = new Set(IMAGE_MODEL_SPECS.map((spec) => spec.modelId));

/** Look up the spec for a model by its id. Returns undefined if not found. */
export function getImageModelSpec(modelId: string): ImageModelSpec | undefined {
  return IMAGE_MODEL_SPECS.find((s) => s.modelId === modelId);
}

/** Maps ImageFormat to MIME types for file validation. */
export const IMAGE_FORMAT_TO_MIME: Record<ImageFormat, string> = {
  PNG: 'image/png',
  JPEG: 'image/jpeg',
  WebP: 'image/webp',
  HEIC: 'image/heic',
  HEIF: 'image/heif',
  BMP: 'image/bmp',
  TIFF: 'image/tiff',
  AVIF: 'image/avif',
};

/** Maps ImageFormat to common file extensions. */
export const IMAGE_FORMAT_TO_EXT: Record<ImageFormat, string[]> = {
  PNG: ['png'],
  JPEG: ['jpg', 'jpeg'],
  WebP: ['webp'],
  HEIC: ['heic'],
  HEIF: ['heif'],
  BMP: ['bmp'],
  TIFF: ['tif', 'tiff'],
  AVIF: ['avif'],
};

export interface FileValidationError {
  type: 'count' | 'format' | 'size';
  fileName: string;
  /** For 'size' errors: actual size in bytes. */
  actualBytes?: number;
  /** For 'size' errors: max allowed bytes. */
  maxBytes?: number;
  /** For 'format' errors: the MIME type that was rejected. */
  mime?: string;
}

/** Validate a list of files against a model spec. Returns all validation errors found. */
export function validateImageFiles(
  files: { name: string; mime: string; size: number }[],
  spec: ImageModelSpec,
): FileValidationError[] {
  const errors: FileValidationError[] = [];
  const allowedMimes = new Set(spec.inputFormats.map((f) => IMAGE_FORMAT_TO_MIME[f]));

  for (const file of files) {
    if (!allowedMimes.has(file.mime)) {
      errors.push({ type: 'format', fileName: file.name, mime: file.mime });
    } else if (file.size > spec.imageSizeMaxBytes) {
      errors.push({
        type: 'size',
        fileName: file.name,
        actualBytes: file.size,
        maxBytes: spec.imageSizeMaxBytes,
      });
    }
  }

  if (files.length > spec.maxInputImages) {
    for (const file of files.slice(spec.maxInputImages)) {
      errors.push({ type: 'count', fileName: file.name });
    }
  }

  return errors;
}
